// letter_card — a single Arabic letter with name, sound, example word.
// Presentational: the learner taps "continue" (handled by the player chrome),
// so this just calls onViewed() on mount.

import { useEffect, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ActivityImage, ArabicText, AudioButton } from './primitives';
import type { ActivityRendererProps } from './types';

export function LetterCard({
  data,
  ctx,
  cb,
}: ActivityRendererProps<content.LetterCardData>): ReactElement {
  const { t } = ctx;
  useEffect(() => {
    cb.onViewed();
    // Fire once on mount; the player records the "viewed" attempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto flex max-w-md flex-col items-center text-center">
      <div className="grid h-44 w-44 place-items-center rounded-[2rem] bg-gradient-to-b from-sun-50 to-cream-50 shadow-[0_24px_60px_-30px_rgba(31,26,23,0.25)] ring-2 ring-sun-300/40">
        <ArabicText
          text={data.glyph}
          ctx={ctx}
          className="font-display-ar text-8xl leading-none text-burgundy-700"
        />
      </div>

      <h2 className="mt-6 text-2xl font-bold text-ink-900">
        <ArabicText text={t(data.nameKey)} ctx={ctx} />
      </h2>
      {data.soundKey ? (
        <p className="mt-1 text-ink-700">
          <ArabicText text={t(data.soundKey)} ctx={ctx} />
        </p>
      ) : null}

      <div className="mt-5">
        <AudioButton src={data.audioName} ctx={ctx} label={t('content.common.play_audio')} />
      </div>

      {data.exampleWordKey ? (
        <div className="mt-8 w-full rounded-3xl bg-white p-5 ring-1 ring-cream-200">
          {data.imageExample ? (
            <div className="mx-auto mb-4 w-32">
              <ActivityImage
                src={data.imageExample}
                ctx={ctx}
                alt={t(data.exampleWordKey)}
                fallbackEmoji="📦"
              />
            </div>
          ) : null}
          <p className="text-xl font-bold text-burgundy-700">
            <ArabicText text={t(data.exampleWordKey)} ctx={ctx} />
          </p>
          {data.exampleGlossKey ? (
            <p className="mt-1 text-sm text-ink-500">{t(data.exampleGlossKey)}</p>
          ) : null}
          <div className="mt-3">
            <AudioButton src={data.audioExample} ctx={ctx} label={t('content.common.play_audio')} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
